/**
 * One timeline entry as a thread message (spec #44 §1.3, #43 §3).
 *
 * `kind` selects the card, `status` + `reason` colour it. REFUSALS AND
 * FAILURES ARE NEVER FILTERED OUT — surfacing them is the point of the
 * record, and a thread of successes only would misrepresent the day.
 *
 * `refs` are deep links (belief, proposal, artifact, specialist) and
 * `steps` expands inline for a cycle. Nothing is summarised by a model on
 * the way to this card: every string here is a stored one.
 */

import { useState } from 'react';
import { Badge, Button, Text } from '@shopify/polaris';
import { Link } from 'react-router-dom';

import type { TimelineEntry, TimelineStatus } from '../../types/timeline';
import { CycleStepsRail } from './CycleStepsRail';
import styles from './workspace.module.css';

const STATUS_CLASS: Record<TimelineStatus, string> = {
  ok: styles.messageOk,
  refused: styles.messageRefused,
  skipped: styles.messageSkipped,
  failed: styles.messageFailed,
  pending: styles.messagePending,
};

const KIND_LABEL: Record<TimelineEntry['kind'], string> = {
  cycle: 'Cycle',
  proposal: 'Proposal',
  evidence: 'Evidence',
  specialist_run: 'Specialist run',
  notification: 'Notification',
};

function statusTone(
  status: TimelineStatus
): 'success' | 'critical' | 'attention' | 'info' | undefined {
  switch (status) {
    case 'ok':
      return 'success';
    case 'failed':
      return 'critical';
    case 'refused':
    case 'skipped':
      return 'attention';
    case 'pending':
      return 'info';
    default:
      return undefined;
  }
}

function time(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

export function TimelineEntryCard({ entry }: { entry: TimelineEntry }) {
  const [expanded, setExpanded] = useState(false);
  const steps = entry.steps ?? [];

  return (
    <article
      className={`${styles.message} ${STATUS_CLASS[entry.status]}`}
      data-testid={`entry-${entry.id}`}
      data-kind={entry.kind}
      data-status={entry.status}
    >
      <div className={styles.messageHead}>
        <Badge tone={statusTone(entry.status)}>{KIND_LABEL[entry.kind]}</Badge>
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {entry.title}
        </Text>
        <span className={styles.messageTime}>{time(entry.occurred_at)}</span>
      </div>

      {entry.reason ? (
        <div className={styles.subtle}>
          <span className={styles.mono}>{entry.reason}</span>
        </div>
      ) : null}

      <div className={styles.subtle}>
        {entry.refs.specialist_key ? (
          <span className={styles.mono}>{entry.refs.specialist_key} </span>
        ) : null}
        {entry.refs.belief_id ? (
          <Link to={`/beliefs?belief=${entry.refs.belief_id}`}>belief</Link>
        ) : null}{' '}
        {entry.refs.proposal_id ? (
          <Link to={`/approvals?highlight=${entry.refs.proposal_id}`}>
            proposal
          </Link>
        ) : null}
        {entry.cost?.cost_usd !== null && entry.cost?.cost_usd !== undefined ? (
          <span className={styles.mono}> ${entry.cost.cost_usd.toFixed(4)}</span>
        ) : null}
      </div>

      {steps.length > 0 ? (
        <>
          <Button
            variant="plain"
            size="slim"
            onClick={() => setExpanded((open) => !open)}
            ariaExpanded={expanded}
            ariaControls={`cycle-steps-${entry.id}`}
          >
            {expanded ? `Hide ${steps.length} steps` : `${steps.length} steps`}
          </Button>
          {expanded ? (
            <CycleStepsRail entryId={entry.id} steps={steps} />
          ) : null}
        </>
      ) : null}
    </article>
  );
}
